import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Check, ArrowLeft, Calendar, Share2, Copy, Mail, Home, ChevronRight } from 'lucide-react';
import BookingModal from '../components/BookingModal';
import SEO from '../components/SEO';
import { getApiEndpoint, getAppUrl } from '../config/api';
import loadingImg from '../assets/loading.png';

const PropertyDetail = () => {
    const { id } = useParams();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [galleryIndex, setGalleryIndex] = useState(0);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [copied, setCopied] = useState(false);
    const [similarProperties, setSimilarProperties] = useState([]);

    const nextGallery = () => {
        if (!property?.media) return;
        setGalleryIndex(prev => (prev === property.media.length - 1 ? 0 : prev + 1));
    };

    const prevGallery = () => {
        if (!property?.media) return;
        setGalleryIndex(prev => (prev === 0 ? property.media.length - 1 : prev - 1));
    };

    useEffect(() => {
        const fetchProperty = async () => {
            try {
                const response = await axios.get(getApiEndpoint(`get_property_details.php?id=${id}`));
                if (response.data.error) {
                    setError(response.data.error);
                } else {
                    setProperty(response.data);
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching property:", err);
                setError("Failed to load property details.");
                setLoading(false);
            }
        };

        fetchProperty();
    }, [id]);

    // Fetch similar properties
    useEffect(() => {
        const fetchSimilar = async () => {
            if (!property) return;
            try {
                const response = await axios.get(getApiEndpoint('get_properties.php'));
                const similar = response.data
                    .filter(p => p.type === property.type && p.id !== property.id)
                    .slice(0, 3);
                setSimilarProperties(similar);
            } catch (err) {
                console.error("Error fetching similar properties:", err);
            }
        };
        fetchSimilar();
    }, [property]);

    const handleShare = (method) => {
        const url = window.location.href;
        const text = `Check out this ${property.type}: ${property.title} - ₹${property.price}/month`;

        switch (method) {
            case 'copy':
                navigator.clipboard.writeText(url);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
                setShowShareMenu(false);
                break;
            case 'whatsapp':
                window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
                break;
            case 'email':
                window.location.href = `mailto:?subject=${encodeURIComponent(property.title)}&body=${encodeURIComponent(text + '\\n\\n' + url)}`;
                break;
        }
    };

    if (loading) return (
        <div className="container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 200px)',
            padding: '4rem 0'
        }}>
            <img
                src={loadingImg}
                alt="Loading"
                style={{ width: '150px', height: '150px', marginBottom: '1rem', animation: 'pulse 2s ease-in-out infinite' }}
            />
            <p style={{ fontSize: '1.25rem', color: 'var(--neutral-600)', fontWeight: '500' }}>Loading property details...</p>
        </div>
    );
    if (error) return <div className="container" style={{ padding: '4rem 0', textAlign: 'center', color: 'red' }}>{error}</div>;
    if (!property) return null;

    return (
        <div style={{ paddingTop: '80px', backgroundColor: '#f4f6f9', minHeight: '100vh' }}>
            <SEO
                title={`${property.title} - ${property.type} for Rent in ${property.location} | Hashtag`}
                description={`${property.description?.substring(0, 155)}... ₹${property.price}/month. ${property.bedrooms} bedrooms, ${property.bathrooms} bathrooms. Book a visit today!`}
                keywords={`${property.type} ${property.location}, rental ${property.location}, ${property.bedrooms} bedroom ${property.type}`}
                url={getAppUrl(`property/${property.id}`)}
                image={property.image_url}
            />

            {/* Breadcrumb Navigation */}
            <div style={{ backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
                <div className="container" style={{ padding: '1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-light)' }}>
                    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--text-light)', textDecoration: 'none' }}>
                        <Home size={16} /> Home
                    </Link>
                    <ChevronRight size={14} />
                    <Link to="/listings" style={{ color: 'var(--text-light)', textDecoration: 'none' }}>Listings</Link>
                    <ChevronRight size={14} />
                    <span style={{ color: 'var(--primary-500)', fontWeight: '500' }}>{property.title}</span>
                </div>
            </div>

            {/* Main Image Header - Full Width Responsive Banner */}
            <div style={{
                height: 'min(60vh, 500px)',
                minHeight: '300px',
                backgroundColor: '#000',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {property.image_url ? (
                    <img
                        src={property.image_url}
                        alt={property.title}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center'
                        }}
                    />
                ) : (
                    <div style={{
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%'
                    }}>No Main Image Available</div>
                )}

                <div style={{ position: 'absolute', top: '2rem', left: '2rem', zIndex: 10 }}>
                    <Link to="/listings" className="btn" style={{ backgroundColor: 'white', color: 'var(--primary-500)', padding: '0.5rem 1rem', display: 'flex', gap: '0.5rem', alignItems: 'center', textDecoration: 'none', borderRadius: '0.5rem', fontWeight: '500' }}>
                        <ArrowLeft size={18} /> Back
                    </Link>
                </div>
            </div>

            <div className="container" style={{ marginTop: '-4rem', position: 'relative', zIndex: 10, paddingBottom: '4rem', padding: '0 1rem' }}>
                <div className="property-content" style={{ backgroundColor: 'white', borderRadius: '1rem', padding: 'clamp(1.5rem, 4vw, 3rem)', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '2rem' }}>
                        <div style={{ flex: '1 1 300px', minWidth: '0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                                <span style={{
                                    backgroundColor: 'var(--primary-500)',
                                    color: 'white',
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '0.25rem',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    textTransform: 'uppercase'
                                }}>
                                    {property.type}
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-light)', fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>
                                    <MapPin size={18} /> {property.location}
                                </span>
                            </div>
                            <h1 style={{ fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', marginBottom: '0.5rem', color: '#1f2937', wordBreak: 'break-word' }}>{property.title}</h1>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                                <span style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                                    Property ID: #{property.id}
                                </span>
                                {property.created_at && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.875rem', color: 'var(--text-light)' }}>
                                        <Calendar size={14} />
                                        Listed {new Date(property.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                            <div style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontWeight: 'bold', color: 'var(--primary-500)' }}>₹{property.price}<span style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)', fontWeight: 'normal', color: 'var(--text-light)' }}>/mo</span></div>
                            <div style={{ color: 'var(--text-light)', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Including all taxes</div>
                        </div>
                    </div>

                    {/* Responsive Grid: Single column on mobile, 2-column on desktop */}
                    <div className="property-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
                        gap: 'clamp(2rem, 4vw, 3rem)'
                    }}>
                        {/* Left Column */}
                        <div>
                            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#374151' }}>Description</h2>
                            <p style={{ color: 'var(--text-light)', lineHeight: '1.8', marginBottom: '3rem' }}>
                                {property.description}
                                <br /><br />
                                Experience luxury living in this meticulously designed space. Featuring modern interiors, ample natural light, and premium furnishings, this property offers the perfect blend of comfort and style.
                            </p>

                            <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#374151' }}>Amenities</h2>
                            {property.amenities && property.amenities.length > 0 ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginBottom: '3rem' }}>
                                    {property.amenities.map((amenity, index) => (
                                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'var(--text-dark)' }}>
                                            <div style={{ backgroundColor: '#f0fdf4', padding: '0.25rem', borderRadius: '50%', color: '#16a34a' }}>
                                                <Check size={14} />
                                            </div>
                                            {amenity}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p style={{ color: 'var(--text-light)', marginBottom: '3rem' }}>No amenities listed for this property.</p>
                            )}

                            {/* Media Gallery Section */}
                            {property.media && property.media.length > 0 && (
                                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '3rem' }}>
                                    <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#374151' }}>Gallery</h2>

                                    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '0.5rem', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                                        {/* Current Slide Display - Responsive Height */}
                                        <div style={{
                                            height: 'clamp(300px, 50vw, 500px)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#000'
                                        }}>
                                            {property.media[galleryIndex]?.media_type === 'video' ? (
                                                <video
                                                    src={property.media[galleryIndex].media_url}
                                                    controls
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <img
                                                    src={property.media[galleryIndex]?.media_url}
                                                    alt="Gallery"
                                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                />
                                            )}
                                        </div>

                                        {/* Navigation Arrows */}
                                        {property.media.length > 1 && (
                                            <>
                                                <button
                                                    onClick={prevGallery}
                                                    aria-label="Previous image"
                                                    style={{
                                                        position: 'absolute',
                                                        left: 'clamp(0.5rem, 2vw, 1rem)',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        color: '#333',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: 'clamp(36px, 8vw, 44px)',
                                                        height: 'clamp(36px, 8vw, 44px)',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                        fontSize: 'clamp(1rem, 3vw, 1.5rem)',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    &#10094;
                                                </button>
                                                <button
                                                    onClick={nextGallery}
                                                    aria-label="Next image"
                                                    style={{
                                                        position: 'absolute',
                                                        right: 'clamp(0.5rem, 2vw, 1rem)',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        color: '#333',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: 'clamp(36px, 8vw, 44px)',
                                                        height: 'clamp(36px, 8vw, 44px)',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                        fontSize: 'clamp(1rem, 3vw, 1.5rem)',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    &#10095;
                                                </button>

                                                {/* Counter */}
                                                <div style={{
                                                    position: 'absolute', bottom: '1rem', right: '1rem',
                                                    backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.75rem'
                                                }}>
                                                    {galleryIndex + 1} / {property.media.length}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Column (Sidebar) - Responsive */}
                        <div>
                            <div style={{
                                backgroundColor: '#f8fafc',
                                padding: 'clamp(1.5rem, 3vw, 2rem)',
                                borderRadius: '1rem',
                                position: window.innerWidth > 768 ? 'sticky' : 'static',
                                top: '100px'
                            }}>
                                <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#1f2937' }}>Interested?</h3>
                                <button
                                    onClick={() => setShowBookingModal(true)}
                                    className="btn"
                                    style={{ width: '100%', marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'var(--primary-500)', color: 'white', border: 'none', borderRadius: '0.5rem', fontWeight: '600', cursor: 'pointer' }}
                                >
                                    Book a Visit
                                </button>
                                <Link
                                    to="/contact"
                                    className="btn btn-outline"
                                    style={{ width: '100%', padding: '0.75rem', border: '1px solid var(--primary-500)', color: 'var(--primary-500)', backgroundColor: 'transparent', borderRadius: '0.5rem', fontWeight: '600', cursor: 'pointer', display: 'block', textAlign: 'center', textDecoration: 'none' }}
                                >
                                    Contact Owner
                                </Link>

                                {/* Share Button */}
                                <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0', position: 'relative' }}>
                                    <button
                                        onClick={() => setShowShareMenu(!showShareMenu)}
                                        style={{
                                            background: 'none',
                                            border: '1px solid #e2e8f0',
                                            cursor: 'pointer',
                                            color: 'var(--text-dark)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            padding: '0.75rem 1rem',
                                            borderRadius: '0.5rem',
                                            width: '100%',
                                            justifyContent: 'center',
                                            fontWeight: '500'
                                        }}
                                    >
                                        <Share2 size={18} /> Share Property
                                    </button>

                                    {/* Share Menu Dropdown */}
                                    {showShareMenu && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            marginTop: '0.5rem',
                                            backgroundColor: 'white',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '0.5rem',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                            zIndex: 10,
                                            overflow: 'hidden'
                                        }}>
                                            <button
                                                onClick={() => handleShare('copy')}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: 'none',
                                                    background: 'none',
                                                    textAlign: 'left',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.75rem',
                                                    color: 'var(--text-dark)',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                <Copy size={16} />
                                                {copied ? 'Link Copied!' : 'Copy Link'}
                                            </button>
                                            <button
                                                onClick={() => handleShare('whatsapp')}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: 'none',
                                                    background: 'none',
                                                    textAlign: 'left',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.75rem',
                                                    color: 'var(--text-dark)',
                                                    fontSize: '0.875rem',
                                                    borderTop: '1px solid #e2e8f0'
                                                }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                                </svg>
                                                Share on WhatsApp
                                            </button>
                                            <button
                                                onClick={() => handleShare('email')}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem 1rem',
                                                    border: 'none',
                                                    background: 'none',
                                                    textAlign: 'left',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.75rem',
                                                    color: 'var(--text-dark)',
                                                    fontSize: '0.875rem',
                                                    borderTop: '1px solid #e2e8f0'
                                                }}
                                            >
                                                <Mail size={16} />
                                                Share via Email
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Similar Properties Section */}
                {similarProperties.length > 0 && (
                    <div className="container" style={{ marginTop: '3rem', paddingBottom: '4rem' }}>
                        <h2 style={{ fontSize: '2rem', marginBottom: '2rem', color: '#1f2937' }}>
                            Similar {property.type} Properties
                        </h2>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            gap: '2rem'
                        }}>
                            {similarProperties.map((similar) => (
                                <Link
                                    key={similar.id}
                                    to={`/property/${similar.id}`}
                                    style={{
                                        textDecoration: 'none',
                                        backgroundColor: 'white',
                                        borderRadius: '0.75rem',
                                        overflow: 'hidden',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <div style={{ height: '200px', overflow: 'hidden', backgroundColor: '#f1f5f9' }}>
                                        <img
                                            src={similar.image_url}
                                            alt={similar.title}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    </div>
                                    <div style={{ padding: '1.5rem' }}>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'start',
                                            marginBottom: '0.75rem'
                                        }}>
                                            <h3 style={{
                                                fontSize: '1.25rem',
                                                fontWeight: '600',
                                                color: '#1f2937',
                                                margin: 0
                                            }}>
                                                {similar.title}
                                            </h3>
                                            <span style={{
                                                backgroundColor: 'var(--primary-500)',
                                                color: 'white',
                                                padding: '0.25rem 0.5rem',
                                                borderRadius: '0.25rem',
                                                fontSize: '0.75rem',
                                                fontWeight: '600'
                                            }}>
                                                {similar.type}
                                            </span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            color: 'var(--text-light)',
                                            fontSize: '0.875rem',
                                            marginBottom: '1rem'
                                        }}>
                                            <MapPin size={16} />
                                            {similar.location}
                                        </div>
                                        <div style={{
                                            fontSize: '1.5rem',
                                            fontWeight: 'bold',
                                            color: 'var(--primary-500)'
                                        }}>
                                            ₹{similar.price}
                                            <span style={{ fontSize: '1rem', fontWeight: 'normal', color: 'var(--text-light)' }}>/mo</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Booking Modal */}
            {showBookingModal && (
                <BookingModal
                    property={property}
                    onClose={() => setShowBookingModal(false)}
                />
            )}
        </div>
    );
};

export default PropertyDetail;
